import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  FormControl,
  Grid,
  Modal,
  Typography,
  CircularProgress,
  Autocomplete,
  TextField,
} from '@mui/material'
import { FormikProps } from 'formik'
import axios from 'axios'
import { DealerGroupData } from '../../types/user'

const normalizeDealerIds = (list: any): string[] => {
  if (!Array.isArray(list)) return []

  return list
    .map((item) => {
      if (typeof item === 'string') return item
      if (item && typeof item === 'object' && 'dealerId' in item) return item.dealerId
      return null
    })
    .filter(Boolean)
}

const denormalizeDealerIds = (ids: string[]) => ids.map((id) => id)

interface FormValues {
  dealerGroupData: DealerGroupData[]
  aftermarketClient: string[]
  email: string
  firstName: string
  lastName: string
  phone: string
}

interface Props {
  openDealerIdModal: boolean
  onCloseDealerIdModal: () => void
  formik: FormikProps<FormValues>
}

const styles = {
  container: {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 700,
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '12px',
    p: 3,
  },
  headerStyle: {
    display: 'flex',
    justifyContent: 'center',
    mb: 2,
  },
  formControlStyles: {
    width: '90%',
    margin: '10px auto',
  },
}

interface DealerGroupWithIds {
  dealerGroupNumber: string
  dealerIdList: { dealerId: string | null }[]
  dealerLogoPath?: string
}

export default function UserDealerIdAdd(props: Props) {
  const { openDealerIdModal, onCloseDealerIdModal, formik } = props
  const [allDealerGroups, setAllDealerGroups] = useState<DealerGroupWithIds[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState<string>('')

  useEffect(() => {
    const fetchDealerGroups = async () => {
      setLoading(true)
      try {
        const token = localStorage.getItem('access_token') // get token from localStorage
        const baseUrl = process.env.REACT_APP_OPW_SERVICE_API_ENDPOINT // use env variable
        const res = await axios.get(`${baseUrl}/dealergroups/getDealerGroupsIds`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        setAllDealerGroups(res.data)
      } catch (error) {
        console.error('Error fetching dealer groups:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchDealerGroups()
  }, [])

  const dealerIdsForGroup =
    allDealerGroups
      .find((g) => g.dealerGroupNumber === selectedGroup)
      ?.dealerIdList.map((d) => d.dealerId || '')
      .sort((a, b) => a.localeCompare(b)) || []

  const handleDealerIdsChange = (ids: string[]) => {
    const sortedIds = [...ids].sort((a, b) => a.localeCompare(b))

    const updated = formik.values.dealerGroupData.map((dg) => {
      if (dg.dealerGroupNumber === selectedGroup) {
        return {
          ...dg,
          dealerIdList: sortedIds.map((id) => ({ dealerId: id })),
        }
      }
      return dg
    })
    formik.setFieldValue('dealerGroupData', updated)
  }

  const userSelectedGroups = formik.values.dealerGroupData.map((g) => g.dealerGroupNumber)

  return (
    <Modal open={openDealerIdModal} onClose={onCloseDealerIdModal}>
      <Box sx={styles.container}>
        <Box sx={styles.headerStyle}>
          <Typography variant='h5'>Dealer ID / Store Number</Typography>
        </Box>

        {loading ? (
          <CircularProgress />
        ) : (
          <Grid container direction='column'>
            <Grid item sx={styles.formControlStyles}>
              <FormControl fullWidth>
                <Autocomplete
                  id='dealer-group-single-autocomplete'
                  options={userSelectedGroups}
                  value={selectedGroup || null}
                  onChange={(event, newValue) => {
                    setSelectedGroup(newValue || '')
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant='outlined'
                      label='Dealer Group'
                      placeholder='Search Dealer Group...'
                    />
                  )}
                />
              </FormControl>
            </Grid>

            {selectedGroup && (
              <Grid item sx={styles.formControlStyles}>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  id='dealer-id-autocomplete'
                  options={dealerIdsForGroup}
                  value={normalizeDealerIds(
                    formik.values.dealerGroupData.find((d) => d.dealerGroupNumber === selectedGroup)
                      ?.dealerIdList,
                  )}
                  onChange={(event, newValue) => handleDealerIdsChange(newValue)}
                  sx={{
                    '& .MuiAutocomplete-inputRoot': {
                      maxHeight: '72px',
                      overflowY: 'auto',
                      flexWrap: 'wrap',
                      alignItems: 'flex-start',
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant='outlined'
                      label='Dealer IDs'
                      placeholder='Search IDs...'
                    />
                  )}
                />
              </Grid>
            )}

            <Box sx={{ textAlign: 'right', mt: 3 }}>
              <Button
                variant='outlined'
                color='error'
                sx={{ mr: 2 }}
                onClick={onCloseDealerIdModal}
              >
                Cancel
              </Button>
              <Button variant='contained' onClick={onCloseDealerIdModal}>
                Submit
              </Button>
            </Box>
          </Grid>
        )}
      </Box>
    </Modal>
  )
}
